import { createElement, type ReactNode } from "react";
import { AlertTriangle, CheckCircle, Info, XCircle } from "lucide-react";
import { toast as sonnerToast, type ExternalToast } from "sonner";
import { toUserMessage } from "@/lib/dashboard-v2/toUserMessage";
import {
  NOTIFICATION_DURATION_MS,
  NOTIFICATION_LOADING_PERSISTENT,
  NOTIFY_DEFAULTS,
  NOTIFY_DEV,
} from "@/lib/dashboard-v2/notificationPolicy";

const isDev = typeof import.meta !== "undefined" && Boolean(import.meta.env?.DEV);

const DEFAULT_NOTIFY_ICONS = {
  success: createElement(CheckCircle, { className: "h-4 w-4 text-emerald-500" }),
  error: createElement(XCircle, { className: "h-4 w-4 text-rose-500" }),
  warning: createElement(AlertTriangle, { className: "h-4 w-4 text-amber-500" }),
  info: createElement(Info, { className: "h-4 w-4 text-sky-500" }),
} as const;

function stripEmojiText(input: string): string {
  return input
    .replace(/\p{Extended_Pictographic}/gu, "")
    .replace(/\uFE0F/gu, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function normalizeOptionalText(input?: string): string | undefined {
  if (!input) return undefined;
  const normalized = stripEmojiText(input);
  return normalized || undefined;
}

function assertTitle(title: string, method: string): string {
  const t = normalizeOptionalText(typeof title === "string" ? title.trim() : "");
  if (!t) {
    if (isDev && NOTIFY_DEV.warnOnMissingTitle) {
      console.warn(`[notify.${method}] Titre obligatoire (reçu vide).`);
    }
    return NOTIFY_DEFAULTS.fallbackTitle;
  }
  if (t.toLowerCase() === "erreur") {
    return "Action impossible";
  }
  return t;
}

/** Options additionnelles (Sonner) + description optionnelle. Toujours fournir `id` pour temps réel, actions en masse, import/export. */
export type NotifyOptions = {
  description?: string;
  id?: string | number;
  icon?: ReactNode;
  action?: { label: string; onClick: () => void };
  cancel?: ExternalToast["cancel"];
  onDismiss?: ExternalToast["onDismiss"];
  onAutoClose?: ExternalToast["onAutoClose"];
  classNames?: ExternalToast["classNames"];
  duration?: number;
};

function buildPayload(
  opts?: string | NotifyOptions,
  defaultDuration = NOTIFICATION_DURATION_MS.success,
  defaultIcon?: ReactNode,
): ExternalToast {
  if (opts === undefined) return { duration: defaultDuration, icon: defaultIcon };
  if (typeof opts === "string") {
    return {
      description: normalizeOptionalText(opts),
      duration: defaultDuration,
      icon: defaultIcon,
    };
  }
  const { description, id, icon, action, cancel, onDismiss, onAutoClose, classNames, duration } = opts;
  return {
    description: normalizeOptionalText(description),
    id,
    icon: icon ?? defaultIcon,
    action,
    cancel,
    onDismiss,
    onAutoClose,
    classNames,
    duration: duration ?? defaultDuration,
  };
}

/**
 * Notifications dashboard unifiées (Sonner uniquement).
 */
export const notify = {
  success(title: string, opts?: string | NotifyOptions) {
    return sonnerToast.success(
      assertTitle(title, "success"),
      buildPayload(opts, NOTIFICATION_DURATION_MS.success, DEFAULT_NOTIFY_ICONS.success),
    );
  },

  error(title: string, opts?: string | NotifyOptions) {
    return sonnerToast.error(
      assertTitle(title, "error"),
      buildPayload(opts, NOTIFICATION_DURATION_MS.error, DEFAULT_NOTIFY_ICONS.error),
    );
  },

  errorFrom(err: unknown, title: string = NOTIFY_DEFAULTS.errorTitle, opts?: Omit<NotifyOptions, "description">) {
    const description = normalizeOptionalText(toUserMessage(err));
    return sonnerToast.error(
      assertTitle(title, "errorFrom"),
      buildPayload({ ...opts, description }, NOTIFICATION_DURATION_MS.error, DEFAULT_NOTIFY_ICONS.error),
    );
  },

  warning(title: string, opts?: string | NotifyOptions) {
    return sonnerToast.warning(
      assertTitle(title, "warning"),
      buildPayload(opts, NOTIFICATION_DURATION_MS.warning, DEFAULT_NOTIFY_ICONS.warning),
    );
  },

  info(title: string, opts?: string | NotifyOptions) {
    return sonnerToast.info(
      assertTitle(title, "info"),
      buildPayload(opts, NOTIFICATION_DURATION_MS.info, DEFAULT_NOTIFY_ICONS.info),
    );
  },

  message(title: string, opts?: string | NotifyOptions) {
    return sonnerToast.message(
      assertTitle(title, "message"),
      buildPayload(opts, NOTIFICATION_DURATION_MS.info, DEFAULT_NOTIFY_ICONS.info),
    );
  },

  loading(title: string, opts?: Omit<NotifyOptions, "duration"> | string) {
    if (typeof opts === "string") {
      return sonnerToast.loading(assertTitle(title, "loading"), {
        description: normalizeOptionalText(opts),
        icon: DEFAULT_NOTIFY_ICONS.info,
        duration: NOTIFICATION_LOADING_PERSISTENT,
      });
    }
    const o = opts ?? {};
    const { description, id, icon, action, cancel, onDismiss, onAutoClose, classNames } = o;
    return sonnerToast.loading(assertTitle(title, "loading"), {
      description: normalizeOptionalText(description),
      id,
      icon: icon ?? DEFAULT_NOTIFY_ICONS.info,
      action,
      cancel,
      onDismiss,
      onAutoClose,
      classNames,
      duration: NOTIFICATION_LOADING_PERSISTENT,
    });
  },

  dismiss(id?: string | number) {
    sonnerToast.dismiss(id);
  },

  custom: sonnerToast.custom.bind(sonnerToast),

  promise<T>(
    promise: Promise<T>,
    msgs: {
      loading: string;
      success: string | ((data: T) => string);
      error?: string | ((err: unknown) => string);
    },
  ) {
    return sonnerToast.promise(promise, {
      loading: assertTitle(msgs.loading, "promise.loading"),
      success:
        typeof msgs.success === "function"
          ? (data: T) => assertTitle((msgs.success as (d: T) => string)(data), "promise.success")
          : assertTitle(msgs.success as string, "promise.success"),
      error:
        typeof msgs.error === "function"
          ? msgs.error
          : (e: unknown) =>
              normalizeOptionalText(toUserMessage(e, typeof msgs.error === "string" ? msgs.error : "Échec de l’opération")) ??
              "Échec de l’opération",
    });
  },
};
